import { AlertType, AlertWithIllustration } from "Components/Form/Alert";
import {
  getCollectionOfDittoComponents,
  useTexts,
} from "Components/TextContext";
import { FieldInputProps, useFormikContext } from "formik";
import React, { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";

import { Icons } from "@storebrand-digital/insurance-definitions";
import { WrapLgMarginBottom } from "App/global-style";
import { ProductBackendName } from "Components/Features/CheckPrice/Products/productTypes";
import { DittoCoverageCard } from "Components/Features/CheckPrice/Tiles/ProductPriceTile/Coverages/DittoCoverages/DittoCoverageCard";
import { getSelectedCoverage } from "Components/Features/CheckPrice/Tiles/ProductPriceTile/Coverages/coverages.helpers";
import { CoveragePriceCalculation } from "Components/Features/CheckPrice/Tiles/ProductPriceTile/Coverages/coverages.types";
import { CheckPriceProps } from "Components/Features/CheckPrice/checkPriceConstants";
import { Checkbox } from "Components/Form/Checkbox";
import { TextComponentNameLevel1 } from "Components/TextContext/textConstants";
import { ThemedSpinner } from "Components/Themes/components/ThemedSpinner";
import { BackendProductInfo } from "Features/CheckPrice/Products/productConstants";
import { useCoveragePriceCalculations } from "Store/priceCalculation/priceCalculationHooks";
import { updatePriceCalculationFormState } from "Store/priceCalculation/priceCalculationSlice";
import { ReduxState } from "Store/rootReducer";
import { isDefined } from "Utils/functions";
import { getError } from "Utils/productUtils";
import { CoverageCheckboxes } from "../ProductIntro/styled";
import { AkaFamilyAccidentPriceRequestInfo } from "../akaFamilyAccidentInterfaces";
import { AKA_FAMILY_ACCIDENT_PRICE_STEP } from "../akaFamilyAccidentSteps";
import { setAkaFamilyAccidentCoverage } from "../akaFamilyAccidentUtils";
import { AkaFamilyAccidentProductPriceFormValues } from "./akaFamilyAccidentProductPriceInterfaces";

export const AkaFamilyAccidentCoverages = ({
  updatePriceRequest,
}: CheckPriceProps): React.ReactElement => {
  const dispatch = useDispatch();
  const priceForm = useFormikContext<AkaFamilyAccidentProductPriceFormValues>();
  const { getText } = useTexts();

  const { isLoading } = useSelector(
    (state: ReduxState) => state.priceCalculation.priceCalculationResult,
  );

  const coverages = useCoveragePriceCalculations();

  const selectedCoverage = (): CoveragePriceCalculation | undefined => {
    const akaFamilyAccidentCoverage = setAkaFamilyAccidentCoverage(
      priceForm.values,
    );
    if (akaFamilyAccidentCoverage) {
      return getSelectedCoverage(akaFamilyAccidentCoverage, coverages);
    }
    return undefined;
  };

  const selectedCoverageValue = selectedCoverage();

  useEffect(() => {
    if (!isLoading) {
      priceForm.setFieldValue(
        "coverage",
        setAkaFamilyAccidentCoverage(priceForm.values),
      );
    }
  }, [isLoading]);

  const { akafamilyaccident: coverageCardTextComponents } =
    getCollectionOfDittoComponents(TextComponentNameLevel1.COVERAGE_CARD);

  const recalculatePrice = (
    e: React.ChangeEvent<HTMLInputElement>,
    field: FieldInputProps<any>,
  ): void => {
    const updatedValues: AkaFamilyAccidentProductPriceFormValues = {
      ...priceForm.values,
      [field.name]: e.target.checked,
    };

    dispatch(
      updatePriceCalculationFormState({
        stepId: AKA_FAMILY_ACCIDENT_PRICE_STEP.id,
        formState: updatedValues,
      }),
    );

    if (updatedValues.hasDisabilityCoverage || updatedValues.hasDeathCoverage) {
      updatePriceRequest<AkaFamilyAccidentPriceRequestInfo>({
        productPriceUrl: BackendProductInfo.AKA_FAMILY_ACCIDENT.priceReqUrl,
        stepForwardOnSuccess: false,
        updatedProductPriceRequestInfo: {
          hasDeathCoverage: updatedValues.hasDeathCoverage,
          hasDisabilityCoverage: updatedValues.hasDisabilityCoverage,
          hasProfessionalDisabilityCoverage:
            updatedValues.hasProfessionalDisabilityCoverage,
        },
      });
    }
  };

  const setHasProfessionalDisabilityCoverage = (
    e: React.ChangeEvent<HTMLInputElement>,
  ): void => {
    if (!e.target.checked) {
      priceForm.setFieldValue("hasProfessionalDisabilityCoverage", false);
    }
  };

  const renderCoverageCard = (): React.ReactElement => {
    if (!isDefined(selectedCoverageValue)) {
      if (isLoading) {
        return <ThemedSpinner />;
      }
      return (
        <AlertWithIllustration
          iconName={Icons.ACCIDENT}
          title={getText("akafamilyaccidentform.alerttitle.nocoverage")}
          alertType={AlertType.INPUT_ERROR_ALERT}
          description={getText(
            "akafamilyaccidentform.alertdescription.nocoverage",
          )}
          marginBottom="32px"
        />
      );
    }
    return (
      <DittoCoverageCard
        key={selectedCoverageValue[0]}
        fieldName="coverage"
        type="radio"
        hideRadioButton
        product={ProductBackendName.AKA_FAMILY_ACCIDENT}
        coverage={selectedCoverageValue[0]}
        textComponents={coverageCardTextComponents}
        prices={selectedCoverageValue[1].prices}
        priceIsLoading={isLoading}
      />
    );
  };

  return (
    <WrapLgMarginBottom>
      <CoverageCheckboxes>
        <Checkbox
          id="hasDeathCoverage"
          name="hasDeathCoverage"
          label={getText("akafamilyaccidentform.label.hasdeathcoverage")}
          onChange={(e, value) => recalculatePrice(e, value)}
        />

        <Checkbox
          id="hasDisabilityCoverage"
          name="hasDisabilityCoverage"
          label={getText("akafamilyaccidentform.label.hasdisabilitycoverage")}
          onChange={(e, value) => {
            setHasProfessionalDisabilityCoverage(e);
            recalculatePrice(e, value);
          }}
          error={getError("hasDisabilityCoverage", priceForm)}
        />
        {priceForm.values.hasDisabilityCoverage && (
          <Checkbox
            id="hasProfessionalDisabilityCoverage"
            name="hasProfessionalDisabilityCoverage"
            label={getText(
              "akafamilyaccidentform.label.hasprofessionaldisabilitycoverage",
            )}
            onChange={(e, value) => recalculatePrice(e, value)}
          />
        )}
      </CoverageCheckboxes>
      {renderCoverageCard()}
    </WrapLgMarginBottom>
  );
};
